import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

import { useSelector } from 'react-redux';

import { useMountedEffect } from '@clatter/platform';
import { CircularLoader } from '../../../shared';
import { viewStateSelectors, authSelectors } from '../../../store';
import { getIconForMimeType } from '../../../helpers';
import { NgCard, NgCardContent, NgPreviewButton, NgTooltip, StatusBadge, StatusBadgeType } from '../../UI';
import { useDnDItem, useItemThumbnail } from '../../../hooks';
import {
  FolderManagerActionTypes,
  FolderManagerItemActionNames,
  FolderManagerItemTypes,
  FolderManagerMimeTypes,
} from '../../../interfaces';

import { ShortcutIcon } from './ShortcutIcon';
import { FileDropContainer } from './FileDropContainer';
import { FolderManagerItemActions } from './FolderManagerItemActions';
import { LayoutCompatibilityBadge } from './LayoutCompatibilityBadge';
import { DefaultThemeBadge } from './DefaultThemeBadge';
import { NgAddButton } from '../../UI/NgAddButton/NgAddButton';

//region INTERFACES
interface FolderManagerGridItemProps {
  item: FolderManagerItem;
  selectedRows?: FolderManagerItem[];
  overId?: string | number | undefined | null;
  isUploadDisabled?: boolean;
  hideActions?: boolean;
  disableDnD?: boolean;
  draggableOnly?: boolean;
  testId?: string;
  compact?: boolean;
  hideTitle?: boolean;
  acceptedDragTypes?: string[];
  thumbnailAssetId?: number; // optional asset ID to use for thumbnail instead of item.id
  showDeletedWarning?: boolean;
  layoutInSelectedTheme?: SlideLayoutSupportIndicator;
  viewStateKey?: string;
  hidePreviewIcon?: boolean;
  addButtonTooltip?: string;
  disabled?: boolean;
  alreadyAdded?: boolean;
  customActionComponent?: React.ReactNode;
  statusBadgeType?: StatusBadgeType;
  highlightBorderColor?: string;
  onSelect?: (data: { event: any; item: any }) => void;
  onAction?: (data: any) => void;
  onDoubleClick?: (data: any) => void;
  onClick?: (data: any) => void;
}

interface GridItemContentProps {
  item: FolderManagerItem;
  isSelected: boolean;
  isOver: boolean;
  setNodeRef: any;
  itemStyle: any;
  attributes: any;
  listeners: any;
  compact?: boolean;
  hideTitle?: boolean;
  shouldDisableDnD: boolean;
  isUploadDisabled?: boolean;
  hideActions?: boolean;
  acceptedDragTypes?: string[];
  thumbnailAssetId?: number;
  showDeletedWarning?: boolean;
  hidePreviewIcon?: boolean;
  addButtonTooltip?: string;
  disabled?: boolean;
  alreadyAdded?: boolean;
  layoutInSelectedTheme?: SlideLayoutSupportIndicator;
  viewStateKey?: string;
  testId?: string;
  customActionComponent?: React.ReactNode;
  statusBadgeType?: StatusBadgeType;
  highlightBorderColor?: string;
  onSelect?: (data: { event: any; item: any }) => void;
  onAction?: (data: any) => void;
  onDoubleClick?: (data: any) => void;
  onClick?: (data: any) => void;
}

interface HeaderTitleProps {
  title: string;
  subtitle?: string;
}
//endregion

//region STYLED
const StyledGridViewItem = styled.li<{
  $isNotAccepted?: boolean;
  $compact?: boolean;
  $disabled?: boolean;
  $alreadyAdded?: boolean;
}>`
  user-select: none;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: auto;
  min-width: 0;

  border-radius: ${({ theme }) => theme.radius.small};

  ${({ $isNotAccepted }) =>
    $isNotAccepted &&
    `
    display: none;
    opacity: 0.5;
    cursor: not-allowed !important;

    &:hover {
      cursor: not-allowed !important;
    }
  `}

  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.4;
    pointer-events: none;
  `}

  ${({ $alreadyAdded }) =>
    $alreadyAdded &&
    `
    cursor: default;

    .item-content {
      opacity: 0.4;
    }
  `}

  .item-header {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.small};

    .title-container {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: ${({ theme }) => theme.spacing.small};
      flex: 1;
      min-width: 0;
    }

    .header-title {
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-width: 0;
      overflow: hidden;
      gap: 2px;

      h4 {
        margin: 0;
        font-size: ${({ theme }) => theme.fontSize.default};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .subtitle {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: ${({ theme }) => theme.fontSize.sm};
        color: ${({ theme }) => theme.palette.grayLighter || '#8c8c8c'};
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .folder-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      justify-content: flex-end;
    }
  }

  .item-content {
    position: relative;
    flex-grow: 1;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    height: auto;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    .image-loader {
      height: 100%;
      width: 100%;
      min-height: 138px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .item-footer {
    display: flex;
    justify-content: flex-end;
  }

  &.over {
    outline: ${({ theme, $compact }) => ($compact ? 'none' : `2px solid ${theme.palette.overBorder}`)};
    outline-offset: -2px;

    .item-content {
      outline: ${({ theme, $compact }) => ($compact ? `2px solid ${theme.palette.overBorder}` : 'none')};
    }
  }

  &:hover {
    cursor: pointer;

    > div:first-of-type {
      background-color: ${({ theme, $compact }) => ($compact ? 'transparent' : theme.palette.hoverBackgroundColor)};
    }

    .show-on-hover {
      display: inline-block;
    }
  }

  background-color: transparent;

  &.selected {
    outline: ${({ theme, $compact }) => ($compact ? 'none' : `2px solid ${theme.palette.black}`)};
    outline-offset: -2px;

    > div:first-of-type {
      background-color: ${({ theme, $compact }) => ($compact ? 'none' : theme.palette.selectedBackgroundColor)};
    }

    .item-content {
      outline: ${({ theme, $compact }) => ($compact ? `2px solid ${theme.palette.black}` : 'none')};
    }
  }

  &.is-folder {
    .item-header {
      h4 {
        top: 2px;
      }
    }

    .item-content {
      display: none;
    }
  }

  &.orphaned {
    opacity: 0.5;
  }

  &.deleted {
    > div:first-of-type {
      background-color: rgba(244, 67, 54, 0.1);
      border: 1px solid rgba(244, 67, 54, 0.3);
    }

    &:hover {
      > div:first-of-type {
        background-color: rgba(244, 67, 54, 0.15);
      }
    }

    &.selected {
      > div:first-of-type {
        background-color: rgba(244, 67, 54, 0.2);
        outline: ${({ theme }) => `2px solid ${theme.palette.black}`};
      }
    }
  }
`;
//endregion

const StyledStatusBadgeSlot = styled.div`
  position: absolute;
  bottom: 4px;
  right: 4px;
  z-index: 2;
`;

const ThumbnailContainer = styled.div<{ $maxWidth?: string }>`
  width: ${({ $maxWidth }) => $maxWidth ?? '100%'};
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.radius.small};
  background-color: ${({ theme }) => theme.palette.grey};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailImage = styled.img<{ $maxWidth?: string }>`
  width: ${({ $maxWidth }) => $maxWidth ?? '100%'};
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 8px;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  opacity: 0.7;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ThemeWarningIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fbc02d;
`;

const StyledCompactItem = styled.div<{ $maxWidth?: string; $highlightBorderColor?: string }>`
  width: 100%;
  max-width: var(--item-max-width, none);
  position: relative;

  .item-header {
    position: absolute;
    top: 4px;
    left: 0;
    right: 0;
    z-index: 2;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    opacity: 0;
    transition: opacity 0.15s;
  }

  .image-loader {
    width: 100%;
    min-height: 0;
  }

  .item-content {
    position: relative;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.radius.button};
    border: 1px solid ${({ $highlightBorderColor, theme }) => $highlightBorderColor || theme.palette.cardBorder};
  }

  ${IconWrapper} {
    width: 28px;
    height: 28px;
  }

  &:hover {
    > div:first-of-type {
      background-color: transparent;
    }

    .item-header {
      opacity: 1;
    }

    .item-content {
      border-color: ${({ theme }) => theme.palette.textLinkColor};
    }
  }

  h5 {
    cursor: pointer;
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;
    margin-top: 3px;
    margin-bottom: none;
    color: ${({ theme }) => theme.palette.textLinkColor};
  }
`;

export const ThumbnailOrIcon = ({ thumbnailUrl, item, maxWidth, mimeTypeIcon }: any) => {
  const hasThumbnail = Boolean(thumbnailUrl);

  return hasThumbnail ? (
    <ThumbnailImage src={thumbnailUrl} $maxWidth={maxWidth} alt={`Thumbnail of ${item?.displayName || item?.name}`} />
  ) : (
    <ThumbnailContainer $maxWidth={maxWidth}>
      <IconWrapper>{mimeTypeIcon || getIconForMimeType(item?.type, item?.isShared)}</IconWrapper>
    </ThumbnailContainer>
  );
};

//region COMPONENTS
const HeaderTitle: React.FC<HeaderTitleProps> = ({ title, subtitle }) => {
  return (
    <div className="header-title">
      <NgTooltip title={title} placement="top-start">
        <h4>{title}</h4>
      </NgTooltip>

      {subtitle && (
        <NgTooltip title={subtitle} placement="top-start">
          <span className="subtitle">{subtitle}</span>
        </NgTooltip>
      )}
    </div>
  );
};

const isItemSelected = (item: FolderManagerItem, selectedRows: FolderManagerItem[] = []) =>
  selectedRows.some((row) => {
    if ((item as any).instanceId && (row as any).instanceId) {
      return (item as any).instanceId === (row as any).instanceId;
    }
    return row.id === item.id && row.type === item.type;
  });

const getTooltipMessage = (isDeleted: boolean, isTrashed: boolean) => {
  if (isDeleted) {
    return 'This asset has been permanently deleted. It will be automatically removed from this presentation.';
  }
  if (isTrashed) {
    return 'This asset has been moved to trash. You can restore it, or the slide will be unavailable if the asset is permanently deleted';
  }
  return '';
};

// memoized component that contain expensive hooks,
// it's needed so we can avoid costly dnd-kit rerenders
const FolderManagerGridItemContent = React.memo(
  ({
    item,
    isSelected,
    isOver,
    setNodeRef,
    itemStyle,
    attributes,
    listeners,
    shouldDisableDnD,
    isUploadDisabled = false,
    hideActions = false,
    acceptedDragTypes,
    thumbnailAssetId,
    showDeletedWarning = false,
    layoutInSelectedTheme,
    viewStateKey,
    hidePreviewIcon = false,
    hideTitle,
    testId = 'folder-manager-grid-item',
    compact,
    addButtonTooltip,
    disabled = false,
    alreadyAdded = false,
    customActionComponent,
    statusBadgeType,
    highlightBorderColor,
    onSelect,
    onAction,
    onDoubleClick,
    onClick,
  }: GridItemContentProps) => {
    const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));
    const isAdmin = useSelector(authSelectors.isAdmin);
    const isContentManager = useSelector(authSelectors.isContentManager);
    const visibilityRef = useRef<HTMLLIElement>(null);
    const isVisibleRef = useRef(false);
    const selectedThemeIdRef = useRef(selectedThemeId);
    selectedThemeIdRef.current = selectedThemeId;

    const [hasBeenVisible, setHasBeenVisible] = useState(false);

    // only update the themeId passed to the query when the item is in the viewport
    // this prevents offscreen items from refetching when theme changes globally
    const [activeThemeId, setActiveThemeId] = useState(selectedThemeId);

    // sync theme when it changes while item is visible
    useEffect(() => {
      if (isVisibleRef.current && activeThemeId !== selectedThemeId) {
        setActiveThemeId(selectedThemeId);
      }
    }, [selectedThemeId]);

    const isFolder = item.type === FolderManagerItemTypes.FOLDER || item.type === FolderManagerItemTypes.WORKSPACE;
    const isPresentationAsset = item.type === FolderManagerItemTypes.PRESENTATION;

    //  itemData can be
    //  AssetSlideFromApi (direct),
    //  AssetSlideFromApi & { slideData } (flattened repo),
    //  SlideFromApi (search — no LayoutName)
    const layoutName: string | null = item.itemData?.slideData?.LayoutName || item.itemData?.LayoutName || null;

    const assetIdForQuery = thumbnailAssetId || (item as any)?.originalId || item?.id;

    const isTypeAccepted = !acceptedDragTypes || isFolder || acceptedDragTypes.includes(item.type);
    const isTrashed = Boolean(item.trashedAt);
    const isDeleted = Boolean(item.deletedAt);
    const isUnavailable = showDeletedWarning && (isTrashed || isDeleted);

    const firstSlide = isPresentationAsset ? item.slides?.[0] : null;
    const shouldSkipThumbnail = isPresentationAsset && !thumbnailAssetId && !firstSlide;

    const {
      thumbnailUrl,
      isLoading: isThumbnailLoading,
      isFetching: isThumbnailFetching,
      isThemeChanging,
      refetch: refetchThumbnail,
    } = useItemThumbnail({
      itemId: assetIdForQuery,
      itemType: shouldSkipThumbnail ? undefined : isPresentationAsset ? FolderManagerMimeTypes.SLIDE : item.type,
      thumbnailUrl: firstSlide?.thumbnailUrl || item.thumbnailUrl,
      themeId: isPresentationAsset ? item.themeId : activeThemeId,
      assetId: thumbnailAssetId,
      enabled: hasBeenVisible,
    });

    const mimeTypeIcon = useMemo(
      () => getIconForMimeType(item?.type, item?.isShared, item?.isSlideRepositoryFolder, item?.isStarterDeckFolder),
      [item?.type, item?.isShared, item?.isSlideRepositoryFolder, item?.isStarterDeckFolder],
    );

    const combinedRef = useCallback(
      (node: HTMLLIElement | null) => {
        visibilityRef.current = node;
        if (!shouldDisableDnD && setNodeRef) {
          setNodeRef(node);
        }
      },
      [shouldDisableDnD, setNodeRef],
    );

    const handleSelect = (event: any) => {
      if (!isTypeAccepted || disabled || alreadyAdded) {
        return;
      }
      // prevent text selection only when shift key is pressed
      if (event.shiftKey) {
        event.preventDefault();
      }
      if (onSelect) {
        onSelect({ event, item });
      }
    };

    const handleClick = (event: any) => {
      if (!isTypeAccepted || disabled || alreadyAdded) {
        return;
      }
      // if (onClick && !event.shiftKey) {
      //   onClick({ event, item });
      // } else {
      handleSelect(event);
      // }
    };

    const handleDoubleClick = (event: any) => {
      if ((item?.trashedAt || item?.deletedAt) && !isFolder) {
        return;
      }

      if (onDoubleClick) {
        onDoubleClick({ event, item });
      }
    };

    // let's properly refetch thumbnail url after asset's prop change
    useMountedEffect(() => {
      if (!shouldSkipThumbnail && refetchThumbnail && thumbnailUrl !== undefined) {
        refetchThumbnail();
      }
    }, [shouldSkipThumbnail, item?.id]);

    // track visibility via ref (no re-renders) and gate initial fetch via state
    useEffect(() => {
      const element = visibilityRef.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          isVisibleRef.current = entry.isIntersecting;

          if (entry.isIntersecting) {
            // gate initial thumbnail fetch
            if (!hasBeenVisible) {
              setHasBeenVisible(true);
            }
            // sync stale theme when scrolling back into view
            setActiveThemeId((prev) => {
              const current = selectedThemeIdRef.current;
              return prev !== current ? current : prev;
            });
          }
        },
        { rootMargin: '200px' },
      );

      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    const gridItem = (
      <StyledGridViewItem
        ref={combinedRef}
        style={shouldDisableDnD ? {} : itemStyle}
        $isNotAccepted={!isTypeAccepted && !isFolder}
        {...(shouldDisableDnD ? {} : attributes)}
        {...(shouldDisableDnD ? {} : listeners)}
        $compact={compact}
        $disabled={disabled}
        $alreadyAdded={alreadyAdded}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={classNames({
          compact: compact,
          over: isOver,
          selected: isSelected,
          'is-folder': item.type === FolderManagerMimeTypes.FOLDER || item.type === FolderManagerMimeTypes.WORKSPACE,
          orphaned: item.sourceId && !item.source,
          deleted: isUnavailable,
        })}
        data-test-id={testId}
      >
        {compact ? (
          <StyledCompactItem $highlightBorderColor={highlightBorderColor}>
            <div className="item-header">
              {!hidePreviewIcon ? (
                <NgPreviewButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.({ actionType: FolderManagerActionTypes.SLIDE_PREVIEW, payload: { item, thumbnailUrl } });
                  }}
                />
              ) : (
                <span />
              )}

              <div className="folder-actions">
                {customActionComponent ??
                  (!hideActions &&
                  item.actions?.some((a: any) => a.name === FolderManagerItemActionNames.ADD_TO_PRESENTATION) ? (
                    <NgAddButton
                      tooltip={addButtonTooltip}
                      remove={alreadyAdded}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction?.({
                          actionType: alreadyAdded
                            ? FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION
                            : FolderManagerItemActionNames.ADD_TO_PRESENTATION,
                          payload: { selectedItems: [item] },
                        });
                      }}
                    />
                  ) : (
                    !hideActions && (
                      <FolderManagerItemActions item={item} onAction={onAction} viewStateKey={viewStateKey} />
                    )
                  ))}
              </div>
            </div>

            <div className="item-content">
              {statusBadgeType && (
                <StyledStatusBadgeSlot>
                  <StatusBadge $compact $type={statusBadgeType} />
                </StyledStatusBadgeSlot>
              )}
              {isThumbnailLoading || (isThemeChanging && isThumbnailFetching) ? (
                <div className="image-loader">{/* <CircularLoader size={3} /> */}</div>
              ) : (
                <ThumbnailOrIcon maxWidth="100%" thumbnailUrl={thumbnailUrl} icon={mimeTypeIcon} item={item} />
              )}
            </div>

            {!hideTitle && <h5>{item?.displayName || item?.name}</h5>}
          </StyledCompactItem>
        ) : (
          <NgCard>
            <NgCardContent>
              {item?.sourceId && <ShortcutIcon />}

              <div className="item-header">
                <div className="title-container">
                  <div className="hedaer-icon">{mimeTypeIcon}</div>

                  <HeaderTitle
                    title={item?.displayName || item?.name}
                    subtitle={!!layoutName ? `Layout: ${layoutName}` : ''}
                  />
                </div>

                <div className="folder-actions">
                  {layoutName && (
                    <LayoutCompatibilityBadge
                      layoutName={layoutName}
                      supportLevel={isAdmin || isContentManager ? layoutInSelectedTheme : undefined}
                      successTooltipTitle="This slide layout is fully supported by the selected theme"
                      errorTooltipTitle="This slide uses a layout that is not supported by the selected theme"
                      warningTooltipTitle="This slide layout is partially supported by the selected theme, some elements may not render correctly"
                      hideLabel
                    />
                  )}

                  {item.presentationTheme?.IsDefault && <DefaultThemeBadge />}

                  {!hideActions && (
                    <FolderManagerItemActions item={item} onAction={onAction} viewStateKey={viewStateKey} />
                  )}
                </div>
              </div>

              <div className="item-content">
                {statusBadgeType && (
                  <StyledStatusBadgeSlot>
                    <StatusBadge $compact $type={statusBadgeType} />
                  </StyledStatusBadgeSlot>
                )}
                {isThumbnailLoading || (isThemeChanging && isThumbnailFetching) ? (
                  <div className="image-loader">
                    <CircularLoader />
                  </div>
                ) : (
                  <ThumbnailOrIcon thumbnailUrl={thumbnailUrl} icon={mimeTypeIcon} item={item} />
                )}
              </div>
            </NgCardContent>
          </NgCard>
        )}
      </StyledGridViewItem>
    );

    const content = isUnavailable ? (
      <NgTooltip title={getTooltipMessage(isDeleted, isTrashed)} placement="top">
        {gridItem}
      </NgTooltip>
    ) : (
      gridItem
    );

    // wrap in FileDropContainer only if upload is enabled and onAction is provided
    if (!isUploadDisabled && onAction) {
      return (
        <FileDropContainer
          isItemContainer
          isDisabled={isUploadDisabled || item.type === FolderManagerMimeTypes.SLIDE}
          onUpload={(fileList) =>
            onAction({
              actionType: FolderManagerActionTypes.UPLOAD,
              payload: { files: fileList, item: item },
            })
          }
        >
          {content}
        </FileDropContainer>
      );
    }

    return content;
  },
  (prev, next) => {
    // compare booleans from DnD
    if (prev.isSelected !== next.isSelected) return false;
    if (prev.isOver !== next.isOver) return false;

    // compare scalar / reference-stable props
    if (prev.item !== next.item) return false;
    if (prev.shouldDisableDnD !== next.shouldDisableDnD) return false;
    if (prev.disabled !== next.disabled) return false;
    if (prev.alreadyAdded !== next.alreadyAdded) return false;
    if (prev.isUploadDisabled !== next.isUploadDisabled) return false;
    if (prev.hideActions !== next.hideActions) return false;
    if (prev.acceptedDragTypes !== next.acceptedDragTypes) return false;
    if (prev.thumbnailAssetId !== next.thumbnailAssetId) return false;
    if (prev.showDeletedWarning !== next.showDeletedWarning) return false;
    if (prev.layoutInSelectedTheme !== next.layoutInSelectedTheme) return false;
    if (prev.viewStateKey !== next.viewStateKey) return false;
    if (prev.testId !== next.testId) return false;
    if (prev.customActionComponent !== next.customActionComponent) return false;
    if (prev.statusBadgeType !== next.statusBadgeType) return false;
    if (prev.highlightBorderColor !== next.highlightBorderColor) return false;

    // skip: callbacks (onSelect, onAction, etc.) — identity may change each render
    // skip: setNodeRef, itemStyle, attributes, listeners — stable refs from dnd-kit

    return true;
  },
);

// outer wrapper — subscribes to dnd-kit context via useDnDItem
// re-renders from dnd-kit context are cheap because this
// component only runs the DnD hook and a selection check.
export const FolderManagerGridItem = ({
  item,
  selectedRows = [],
  overId,
  isUploadDisabled,
  hideActions,
  disableDnD = false,
  draggableOnly = false,
  acceptedDragTypes,
  thumbnailAssetId,
  showDeletedWarning,
  layoutInSelectedTheme,
  viewStateKey,
  testId,
  compact,
  hideTitle = false,
  hidePreviewIcon = false,
  addButtonTooltip,
  disabled = false,
  alreadyAdded = false,
  customActionComponent,
  statusBadgeType,
  highlightBorderColor,
  onSelect,
  onAction,
  onDoubleClick,
  onClick,
}: FolderManagerGridItemProps) => {
  const isFolder = item.type === FolderManagerItemTypes.FOLDER || item.type === FolderManagerItemTypes.WORKSPACE;
  const isTypeAccepted = !acceptedDragTypes || isFolder || acceptedDragTypes.includes(item.type);
  const shouldDisableDnD = disableDnD || !isTypeAccepted;

  const dndHooks = useDnDItem({
    item: item,
    overId: overId,
    selectedRows: selectedRows,
    thumbnailUrl: undefined, // thumbnail is resolved inside the content component
    sortable: !draggableOnly,
    disabled: shouldDisableDnD,
  });

  const { isOver, isSelected, listeners, attributes, setNodeRef, itemStyle } = dndHooks ?? {
    isOver: false,
    isSelected: isItemSelected(item, selectedRows),
    listeners: {},
    attributes: {},
    setNodeRef: null,
    itemStyle: {},
  };

  return (
    <FolderManagerGridItemContent
      item={item}
      isSelected={isSelected}
      isOver={isOver}
      setNodeRef={setNodeRef}
      itemStyle={itemStyle}
      attributes={attributes}
      listeners={listeners}
      shouldDisableDnD={shouldDisableDnD}
      isUploadDisabled={isUploadDisabled}
      hideActions={hideActions}
      acceptedDragTypes={acceptedDragTypes}
      thumbnailAssetId={thumbnailAssetId}
      showDeletedWarning={showDeletedWarning}
      layoutInSelectedTheme={layoutInSelectedTheme}
      viewStateKey={viewStateKey}
      hidePreviewIcon={hidePreviewIcon}
      testId={testId}
      hideTitle={hideTitle}
      addButtonTooltip={addButtonTooltip}
      disabled={disabled}
      alreadyAdded={alreadyAdded}
      customActionComponent={customActionComponent}
      statusBadgeType={statusBadgeType}
      highlightBorderColor={highlightBorderColor}
      onSelect={onSelect}
      onAction={onAction}
      onDoubleClick={onDoubleClick}
      onClick={onClick}
      compact={compact}
    />
  );
};
//endregion
